import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { api, ApiError } from '../api/index.js';
import type { components } from '../api/schema.gen.js';
import { queryKeys } from '../query-client.js';
import { hasAnyPermission, hasPermission, type Permission, type Scope } from './permissions.js';

export type Session = components['schemas']['Session'];
export type AuthenticatedUser = components['schemas']['AuthenticatedUser'];

interface AuthContextValue {
  session: Session | null;
  user: AuthenticatedUser | null;
  status: UseQueryResult['status'];
  isAuthenticated: boolean;
  /** Re-reads the session, e.g. after acknowledging a policy. */
  refresh: () => Promise<void>;
  /** Ends the session on the server and clears every cached query. */
  signOut: () => Promise<void>;
  can: (permission: Permission, scope?: Scope) => boolean;
  canAny: (permissions: readonly Permission[], scope?: Scope) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Where `setTimeout` overflows its 32-bit delay and fires immediately. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Loads and holds the signed-in session.
 *
 * The session is a normal query rather than bespoke state, which buys three
 * things for free: it is cached, it deduplicates concurrent reads, and it can
 * be invalidated by any code that changes something the session reports —
 * acknowledging a policy, enrolling a second factor, changing a password.
 *
 * There is no token here and nothing in `localStorage`. The browser holds an
 * HttpOnly cookie; this query simply asks the server who that cookie belongs
 * to. A 401 therefore means "not signed in", which is an answer, not an error.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const query = useQuery<Session | null>({
    queryKey: queryKeys.session,
    queryFn: async () => {
      try {
        return await api.get<Session>('/auth/session');
      } catch (error) {
        // Not signed in is the expected state for a first-time visitor.
        if (ApiError.is(error) && error.status === 401) return null;
        throw error;
      }
    },
    // The session gates every screen, so it must not be served stale after a
    // role change or a sign-out in another tab.
    staleTime: 0,
    retry: false,
    /**
     * The one query in the app that does refetch on focus.
     *
     * Everything else stays put — an HRMS's data moves slowly and refetching
     * a holiday calendar on every tab switch costs bandwidth for nothing. But
     * coming back to a tab is exactly the moment to find out whether the
     * session survived: without this, a session that ended an hour ago left
     * the shell still showing somebody's name, their menus and their data
     * until they happened to reload the page by hand.
     */
    refetchOnWindowFocus: true,
  });

  const session = query.data ?? null;

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // The server may already consider us signed out, or be unreachable.
      // Either way the user asked to sign out, so the local state is cleared
      // regardless — failing to do that is the outcome that actually matters.
    }

    /**
     * Order matters here, and getting it wrong is why sign-out silently did
     * nothing.
     *
     * `queryClient.clear()` removes every query **including the session query
     * the route guards are subscribed to**, tearing down the observer. Writing
     * `null` afterwards then lands in a fresh cache entry that nothing is
     * listening to, so `isAuthenticated` never flips and the user stays on the
     * page they just signed out of.
     *
     * So: mark the session as signed out first, while the observer is still
     * live, and only then drop the other queries — which is what stops another
     * employee's data sitting in memory on a shared machine.
     */
    queryClient.setQueryData(queryKeys.session, null);
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== queryKeys.session[0],
    });
  }, [queryClient]);

  /**
   * Re-reads the session at the moment it could have expired.
   *
   * Nothing else ever asks. The session query answers once on load, so a
   * browser left open on a page that makes no requests would sit there signed
   * in long after the server had stopped agreeing — the sign-in screen
   * appeared only on a manual reload. A phone hides that because the OS
   * discards the tab and reloads it, which is why the same account appeared to
   * log itself out on mobile and never on the desktop.
   *
   * `idleExpiresAt` is a snapshot, and the server pushes it out on every
   * request. So for somebody who is active this fires early, finds the session
   * alive, and reschedules from the newer answer; for somebody who is not, it
   * fires just after the real expiry and the 401 sends them to sign in.
   */
  useEffect(() => {
    if (!session) return;

    const expiresAt = Math.min(
      Date.parse(session.idleExpiresAt),
      Date.parse(session.absoluteExpiresAt),
    );
    if (!Number.isFinite(expiresAt)) return;

    // A second past the deadline, so a clock a hair fast does not ask while
    // the session is still valid and reschedule itself into a tight loop.
    const delay = Math.max(expiresAt - Date.now(), 0) + 1_000;
    // `setTimeout` silently fires straight away above ~24.8 days. Nothing in
    // Orbit lasts that long, but a clamp costs one line and the failure it
    // prevents is a request loop.
    if (delay > MAX_TIMEOUT_MS) return;

    const timer = setTimeout(() => void refresh(), delay);
    return () => clearTimeout(timer);
  }, [session, refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      status: query.status,
      isAuthenticated: session !== null,
      refresh,
      signOut,
      can: (permission, scope) => hasPermission(session?.permissions, permission, scope),
      canAny: (permissions, scope) => hasAnyPermission(session?.permissions, permissions, scope),
    }),
    [session, query.status, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/**
 * The session where there may not be one to read.
 *
 * For the handful of components that sit at the very edge of the tree and are
 * also rendered on their own — the screen wrapper, which a test can mount
 * without an app around it. Callers treat `null` as *no answer*, never as
 * permission: a missing provider means nothing has been checked, which is a
 * reason to refuse rather than to allow.
 */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext);
}

/**
 * The signed-in user, for screens that are already behind a route guard.
 * Throws rather than returning null so a component does not have to
 * null-check something the router has already guaranteed.
 */
export function useCurrentUser(): AuthenticatedUser {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser used outside an authenticated route');
  return user;
}
